// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.DefaultChartOptionsMapping.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------
import { MappingValues } from 'logic/data/Izenda.ChartDefaultConfig';

const {
  LABEL_ROTATION_VALUES,
  HORIZONTAL_ALIGN_VALUES,
  VERTICAL_ALIGN_VALUES,
  LINE_STYLE_VALUES,
  AXIS_ALIGN_VALUES,
  LAYOUT_ALIGN_VALUES
} = MappingValues;

export const COMMON_OPTIONS = {
  izHeight: { propKey: 'chart.height', defaultValue: undefined },
  izWidth: { propKey: 'chart.width', defaultValue: undefined },
  izMargin: { propKey: 'chart.margin', defaultValue: undefined },
  izDisableMultiColor: { defaultValue: false },
  izColorPattern: { propKey: 'colors', defaultValue: undefined },
  'izEntire.borderColor': { defaultValue: undefined },
  'izEntire.borderWidth': { defaultValue: undefined },
  'izEntire.borderStyle': { defaultValue: undefined },
  'izEntire.backgroundColor': { propKey: 'chart.backgroundColor', defaultValue: undefined },
  'izPlot.borderColor': { propKey: 'chart.plotBorderColor', defaultValue: undefined },
  'izPlot.borderWidth': { propKey: 'chart.plotBorderWidth', defaultValue: undefined },
  'izPlot.backgroundColor': { propKey: 'chart.plotBackgroundColor', defaultValue: undefined },
  izHoverLabels: { propKey: 'plotOptions.series.states.hover.enabled', defaultValue: true },
  izLabelOrientation: { propKey: 'plotOptions.series.dataLabels.rotation', propSource: LABEL_ROTATION_VALUES },
  'izLegend.visibility': { propKey: 'legend.enabled', defaultValue: false },
  'izLegend.horizontalAlign': { propKey: 'legend.align', propSource: HORIZONTAL_ALIGN_VALUES, defaultValue: 'izRight' },
  'izLegend.verticalAlign': {
    propKey: 'legend.verticalAlign',
    propSource: VERTICAL_ALIGN_VALUES,
    defaultValue: 'izBottom'
  },
  'izLegend.borderVisible': { propKey: 'legend.borderVisible', defaultValue: false },
  'izLegend.borderColor': { propKey: 'legend.borderColor', defaultValue: undefined },
  'izLegend.borderWidth': { propKey: 'legend.borderWidth', defaultValue: 0 },
  'izLegend.fontStyling': { propKey: 'legend.itemStyle', defaultValue: undefined },
  'izLegend.title': { propKey: 'legend.title', defaultValue: undefined },
  'izLegend.layout': { propKey: 'legend.layout', propSource: LAYOUT_ALIGN_VALUES, defaultValue: undefined },
  'izLegend.backgroundColor': { propKey: 'legend.backgroundColor', defaultValue: undefined },
  izDataRefreshInterval: { defaultValue: undefined },
  'izMarker.radius': { propKey: 'plotOptions.series.marker.radius', defaultValue: undefined },
  izChartStyle: { propKey: 'chart.style', defaultValue: {} },
  izendaHiddenAllAxis: { propKey: 'izendaOptions.izendaHiddenAllAxis', defaultValue: false },
  'izTooltip.enabled': { propKey: 'tooltip.enabled', defaultValue: undefined },
  'izTooltip.backgrounColor': { propKey: 'tooltip.backgroundColor', defaultValue: undefined },
  'izTooltip.borderWidth': { propKey: 'tooltip.borderWidth', defaultValue: undefined },
  'izTooltip.shadow': { propKey: 'tooltip.shadow', defaultValue: undefined },
  'izTooltip.useHTML': { propKey: 'tooltip.useHTML', defaultValue: undefined },
  'izTooltip.hideDelay': { propKey: 'tooltip.hideDelay', defaultValue: undefined },
  'izTooltip.shared': { propKey: 'tooltip.shared', defaultValue: undefined },
  'izTooltip.padding': { propKey: 'tooltip.backgroundColor', defaultValue: undefined },
  'izTooltip.positioner': { propKey: 'tooltip.positioner', defaultValue: undefined },
  izFillContainer: { propKey: 'izendaOptions.fillContainer' }
};

export const THRESHOLDS_LINE = {
  'izLabel.text': {
    propKey: 'label.text',
    defaultValue: ''
  },
  'izLabel.horizontalAlign': {
    propKey: 'label.align',
    propSource: HORIZONTAL_ALIGN_VALUES,
    defaultValue: HORIZONTAL_ALIGN_VALUES.izLeft
  },
  'izLabel.verticalAlign': {
    propKey: 'label.verticalAlign',
    propSource: VERTICAL_ALIGN_VALUES,
    defaultValue: VERTICAL_ALIGN_VALUES.izTop
  },
  'izLabel.style': {
    propKey: 'label.style',
    propSource: LINE_STYLE_VALUES,
    defaultValue: {}
  },
  izValue: {
    propKey: 'value',
    defaultValue: 0
  },
  izWidth: {
    propKey: 'width',
    defaultValue: 0
  },
  izDashStyle: {
    propKey: 'dashStyle',
    propSource: LINE_STYLE_VALUES,
    defaultValue: LINE_STYLE_VALUES.izSolid
  },
  izIndex: {
    propKey: 'zIndex',
    defaultValue: 1
  },
  izThresholdColor: {
    propKey: 'color',
    defaultValue: ''
  },
  'izLabel.textAlign': {
    propKey: 'label.textAlign',
    defaultValue: ''
  }
};

export const AXIS_OPTIONS = {
  /* label */
  izLabelOrientation: {
    propKey: 'labels.rotation',
    propSource: LABEL_ROTATION_VALUES,
    defaultValue: null
  },
  'izLabel.enabled': {
    propKey: 'labels.enabled',
    defaultValue: null
  },
  izStartOnTick: {
    propKey: 'startOnTick',
    defaultValue: true
  },
  izEndOnTick: {
    propKey: 'endOnTick',
    defaultValue: true
  },
  izTickPositions: {
    propKey: 'tickPositions',
    defaultValue: null
  } /* axis */,
  'izTitle.enabled': {
    propKey: 'title.enabled',
    defaultValue: true
  },
  'izTitle.text': {
    propKey: 'title.text',
    defaultValue: ''
  },
  'izTitle.style': {
    propKey: 'title.style',
    defaultValue: null
  },
  'izTitle.align': {
    propKey: 'title.align',
    propSource: AXIS_ALIGN_VALUES,
    defaultValue: AXIS_ALIGN_VALUES.izLeft
  },
  izTickInterval: {
    propKey: 'tickInterval',
    defaultValue: 0
  },
  izTickCategoryInterval: {
    propKey: 'tickCategoryInterval',
    defaultValue: 0
  },
  izStartingPoint: {
    propKey: 'min',
    defaultValue: 0
  },
  izEndingPoint: {
    propKey: 'max',
    defaultValue: null
  },
  izCategory: {
    defaultValue: false
  },
  izOpposite: {
    propKey: 'opposite',
    defaultValue: null
  },
  /* grid */
  'izGrid.lineWidth': {
    propKey: 'gridLineWidth',
    defaultValue: 1
  },
  'izGrid.lineColor': {
    propKey: 'gridLineColor',
    defaultValue: ''
  },
  'izGrid.lineDashStyle': {
    propKey: 'gridLineDashStyle',
    propSource: LINE_STYLE_VALUES,
    defaultValue: LINE_STYLE_VALUES.izSolid
  }
};



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/Fields/ReportBody/ReportPartType/ReportPartChart/Izenda.DefaultChartOptionsMappings.js